/**
 * Admin Dashboard - Cetalingua FM
 * Target path: /admin-panel/src/components/AdminDashboard.js
 * REPLACES previous version
 *
 * Changes:
 * - REMOVED: All legacy listings references (pending-listings tab, loadPendingListings, handlers)
 * - REMOVED: All legacy pushNotificationRequests references
 * - NEW: "Live Content" tab showing all approved content + approved push notifications
 * - NEW: When approving content, old content of same type+advertiser gets archived
 * - Admin can see what's live; when advertiser deletes, it disappears
 */

import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase-config';
import { signOut } from 'firebase/auth';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  Timestamp,
  orderBy 
} from 'firebase/firestore';

import AdminDetections from './admin/AdminDetections';
import AdminStations from './admin/AdminStations';
import AdminDailyQuiz from './admin/AdminDailyQuiz';
import AdminQuizPractice from './admin/AdminQuizPractice';
import AdminFreeAccess from './admin/AdminFreeAccess';
import AdminPromoRequests from './admin/AdminPromoRequests';
import AdminAppUsers from './admin/AdminAppUsers';
import { CONTENT_TYPES } from '../shared/advertiserConstants';
import { getSpeciesSummary } from '../shared/speciesUtils';
import './AdminDashboard.css';

export default function AdminDashboard({ user }) {
  const [activeTab, setActiveTab] = useState('content-review');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [pendingContent, setPendingContent] = useState([]);
  const [liveContent, setLiveContent] = useState([]);
  const [approvedPush, setApprovedPush] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [proofRequests, setProofRequests] = useState([]);
  const [pushRequests, setPushRequests] = useState([]);

  // Advertiser pushes approved this week, Monday–Sunday (26 Oct 2026): at most 2 per week across all advertisers
  const PUSH_WEEKLY_LIMIT = 2;
  const [pushThisWeek, setPushThisWeek] = useState(0);
  const countPushThisWeek = async () => {
    try {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
      const snap = await getDocs(query(collection(db, 'pushNotifications'), where('status', 'in', ['approved', 'sent'])));
      setPushThisWeek(snap.docs.filter((d) => {
        const t = d.data().approvedAt;
        const dt = t && t.toDate ? t.toDate() : null;
        return dt && dt >= start;
      }).length);
    } catch (e) { console.warn('push week counter:', e.message); }
  };
  
  // Stats
  const [stats, setStats] = useState({
    pendingContent: 0,
    pendingProofs: 0,
    pendingPromo: 0,
    pendingPush: 0,
    liveContent: 0,
    totalAdvertisers: 0,
    activeStations: 0,
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    const automatedTabs = ['detections', 'stations', 'daily-quiz', 'quiz-practice', 'free-access', 'promo-requests', 'app-users'];
    if (automatedTabs.includes(activeTab)) {
      await loadStats();
      return;
    }

    setLoading(true);
    try {
      switch (activeTab) {
        case 'content-review': await loadPendingContent(); break;
        case 'live-content': await loadLiveContent(); break;
        case 'advertisers': await loadAdvertisers(); break;
        case 'proof-review': await loadProofRequests(); break;
        case 'push-notifications': await loadPushRequests(); break;
        default: break;
      }
      await loadStats();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Pending content
      const contentQ = query(collection(db, 'advertiserContent'), where('status', '==', 'pending'));
      const contentSnap = await getDocs(contentQ);

      // Live content
      const liveQ = query(collection(db, 'advertiserContent'), where('status', '==', 'approved'));
      const liveSnap = await getDocs(liveQ);

      // Pending proofs
      const proofsQ = query(collection(db, 'users'), where('proofStatus', '==', 'pending'));
      const proofsSnap = await getDocs(proofsQ);
      
      // Promo materials to mail (sticker program)
      let promoCount = 0;
      try {
        const promoSnap = await getDocs(query(collection(db, 'users'), where('promoRequest.status', '==', 'requested')));
        promoCount = promoSnap.size;
      } catch (e) { /* none yet */ }

      // Pending push
      let pushCount = 0;
      try {
        const pushQ = query(collection(db, 'pushNotifications'), where('status', '==', 'pending'));
        const pushSnap = await getDocs(pushQ);
        pushCount = pushSnap.size;
      } catch (e) { /* collection may not exist yet */ }
      
      // Total advertisers
      const advertisersQ = query(collection(db, 'users'), where('role', '==', 'advertiser'));
      const advertisersSnap = await getDocs(advertisersQ);

      // Active Stations
      const stationsQ = query(collection(db, 'stations'), where('isActive', '==', true));
      const stationsSnap = await getDocs(stationsQ);
      
      setStats({
        pendingContent: contentSnap.size,
        liveContent: liveSnap.size,
        pendingProofs: proofsSnap.size,
        pendingPromo: promoCount,
        pendingPush: pushCount,
        totalAdvertisers: advertisersSnap.size,
        activeStations: stationsSnap.size,
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadPendingContent = async () => {
    const q = query(
      collection(db, 'advertiserContent'),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    setPendingContent(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  // NEW: Load approved content + approved push for "Live Content" tab
  const loadLiveContent = async () => {
    // Approved content
    const contentQ = query(
      collection(db, 'advertiserContent'),
      where('status', '==', 'approved'),
      orderBy('approvedAt', 'desc')
    );
    const contentSnap = await getDocs(contentQ);
    setLiveContent(contentSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    // Approved push notifications
    try {
      const pushQ = query(
        collection(db, 'pushNotifications'),
        where('status', '==', 'approved'),
        orderBy('approvedAt', 'desc')
      );
      const pushSnap = await getDocs(pushQ);
      setApprovedPush(pushSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setApprovedPush([]);
    }
  };

  const loadAdvertisers = async () => {
    const q = query(collection(db, 'users'), where('role', '==', 'advertiser'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    setAdvertisers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const loadProofRequests = async () => {
    const q = query(collection(db, 'users'), where('proofStatus', '==', 'pending'), orderBy('proofUploadedAt', 'desc'));
    const snapshot = await getDocs(q);
    setProofRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const loadPushRequests = async () => {
    const results = [];
    try {
      const q = query(collection(db, 'pushNotifications'), where('status', '==', 'pending'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      snap.docs.forEach(d => results.push({ id: d.id, ...d.data() }));
    } catch (e) { /* collection may not exist */ }
    setPushRequests(results);
    countPushThisWeek();
  };

  // Approve content — archives old content of same type+advertiser
  const handleApproveContent = async (content) => {
    if (!window.confirm(`Approve "${content.title}" from ${content.advertiserName}?`)) return;
    try {
      // Archive any existing approved content of same type from same advertiser
      const existingQ = query(
        collection(db, 'advertiserContent'),
        where('advertiserId', '==', content.advertiserId),
        where('type', '==', content.type),
        where('status', '==', 'approved')
      );
      const existingSnap = await getDocs(existingQ);
      
      const archivePromises = existingSnap.docs.map(d =>
        updateDoc(doc(db, 'advertiserContent', d.id), {
          status: 'archived',
          isActive: false,
          archivedAt: Timestamp.now(),
          archivedReason: 'replaced_by_new',
          updatedAt: Timestamp.now(),
        })
      );
      await Promise.all(archivePromises);

      // Approve the new one
      await updateDoc(doc(db, 'advertiserContent', content.id), {
        status: 'approved',
        isActive: true,
        approvedAt: Timestamp.now(),
        approvedBy: user.email,
        publishedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      loadData();
    } catch (error) {
      console.error(error);
      alert('Failed to approve content');
    }
  };

  const handleRejectContent = async (content) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await updateDoc(doc(db, 'advertiserContent', content.id), {
        status: 'rejected',
        isActive: false,
        rejectionReason: reason,
        rejectedAt: Timestamp.now(),
        rejectedBy: user.email,
        updatedAt: Timestamp.now(),
      });
      loadData();
    } catch (error) {
      console.error(error);
      alert('Failed to reject content');
    }
  };

  const handleApproveProof = async (advertiser) => {
    // the free 3 months are once per account (1 Oct 2026)
    if (advertiser.stickerProgramUsedAt) {
      alert(`"${advertiser.companyName}" already used the free program. Reject this proof or give free access by hand (🎁 Free access).`);
      return;
    }
    if (!window.confirm(`Approve proof from "${advertiser.companyName}"? This will give them 3 months free.`)) return;
    try {
      const expiry = new Date();
      expiry.setMonth(expiry.getMonth() + 3);
      
      await updateDoc(doc(db, 'users', advertiser.id), {
        proofStatus: 'approved',
        proofApprovedAt: Timestamp.now(),
        proofApprovedBy: user.email,
        subscriptionExpiry: Timestamp.fromDate(expiry),
        // Stage 3: the free 3 months are an access grant -> the portal functions add "advertiser" to products
        // and remove it again when the grant ends
        accessGrant: {
          products: ['advertiser'],
          until: Timestamp.fromDate(expiry),
          grantedBy: user.email,
          grantedAt: Timestamp.now(),
          note: 'Sticker program',
        },
        stickerProgramUsedAt: Timestamp.now(),
      });
      loadData();
    } catch (error) {
      console.error(error);
      alert('Failed to approve proof');
    }
  };

  const handleRejectProof = async (advertiser) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await updateDoc(doc(db, 'users', advertiser.id), {
        proofStatus: 'rejected',
        proofRejectionReason: reason,
        proofRejectedAt: Timestamp.now(),
        proofRejectedBy: user.email,
      });
      loadData();
    } catch (error) {
      console.error(error);
      alert('Failed to reject proof');
    }
  };

  const handleApprovePush = async (request) => {
    if (pushThisWeek >= PUSH_WEEKLY_LIMIT && !window.confirm(`⚠️ ${pushThisWeek} partner push notifications were already approved this week (limit ${PUSH_WEEKLY_LIMIT}).\n\nBest: leave this one pending and approve it next week.\n\nApprove anyway?`)) return;
    if (!window.confirm(`Approve push notification from "${request.advertiserName}"?`)) return;
    try {
      await updateDoc(doc(db, 'pushNotifications', request.id), {
        status: 'approved',
        approvedAt: Timestamp.now(),
        approvedBy: user.email,
      });

      // (Oct 2026) lastPushSentAt is now written by the sending function AFTER the push went out. Writing it here
      // made the function think the monthly push was already used and reject every approved push.
      await updateDoc(doc(db, 'users', request.advertiserId), {
        pendingNotification: null,
      });

      loadData();
      alert('Push notification approved!');
    } catch (error) {
      console.error(error);
      alert('Failed to approve push');
    }
  };

  // 📲 Send test to my phone (Oct 2026): only to the app account with this email, marked TEST; stays pending
  const handleTestPush = async (request) => {
    let last = '';
    try { last = window.localStorage.getItem('ctTestPushEmail') || ''; } catch (e) { /* no storage */ }
    const email = window.prompt(
      'Send a TEST of this push only to your own phone.\n\nEmail of YOUR account in the Cetalingua FM app (logged in on your phone, notifications allowed):', last);
    if (!email || !email.trim()) return;
    try { window.localStorage.setItem('ctTestPushEmail', email.trim()); } catch (e) { /* no storage */ }
    try {
      await updateDoc(doc(db, 'pushNotifications', request.id), {
        testRequest: { email: email.trim(), byUid: user.uid, requestedAt: Timestamp.now() },
      });
      setTimeout(loadData, 6000); // the result appears on the card after a few seconds
      alert('Test is being sent. The result shows on this card in a few seconds.');
    } catch (error) {
      console.error(error);
      alert('Could not start the test: ' + error.message);
    }
  };

  const handleRejectPush = async (request) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      await updateDoc(doc(db, 'pushNotifications', request.id), {
        status: 'rejected',
        rejectionReason: reason,
        rejectedAt: Timestamp.now(),
        rejectedBy: user.email,
      });
      loadData();
    } catch (error) {
      console.error(error);
      alert('Failed to reject push');
    }
  };

  const handleLogout = () => {
    signOut(auth);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getContentTypeLabel = (type) => {
    const found = CONTENT_TYPES.find(t => t.id === type);
    return found ? `${found.emoji} ${found.name}` : type;
  };

  const displaySpecies = (speciesArray) => {
    if (!speciesArray || speciesArray.length === 0) return 'None';
    if (typeof speciesArray[0] === 'number') {
      return getSpeciesSummary(speciesArray, 3);
    }
    return speciesArray.join(', ');
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="header-left">
          <h1>🐋 Cetalingua FM Admin</h1>
        </div>
        <div className="header-right">
          <span className="user-email">{user.email}</span>
          <button onClick={handleLogout} className="signout-btn">Sign Out</button>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-item" onClick={() => setActiveTab('content-review')}>
          <span className="stat-value">{stats.pendingContent}</span>
          <span className="stat-label">Pending Content</span>
        </div>
        <div className="stat-item" onClick={() => setActiveTab('live-content')}>
          <span className="stat-value">{stats.liveContent}</span>
          <span className="stat-label">Live Content</span>
        </div>
        <div className="stat-item" onClick={() => setActiveTab('proof-review')}>
          <span className="stat-value">{stats.pendingProofs}</span>
          <span className="stat-label">Pending Proofs</span>
        </div>
        <div className="stat-item" onClick={() => setActiveTab('promo-requests')}>
          <span className="stat-value">{stats.pendingPromo}</span>
          <span className="stat-label">Promo to Mail</span>
        </div>
        <div className="stat-item" onClick={() => setActiveTab('push-notifications')}>
          <span className="stat-value">{stats.pendingPush}</span>
          <span className="stat-label">Pending Push</span>
        </div>
        <div className="stat-item" onClick={() => setActiveTab('advertisers')}>
          <span className="stat-value">{stats.totalAdvertisers}</span>
          <span className="stat-label">Total Advertisers</span>
        </div>
        <div className="stat-item" onClick={() => setActiveTab('stations')}>
          <span className="stat-value">{stats.activeStations}</span>
          <span className="stat-label">Active Stations</span>
        </div>
      </div>

      {/* Navigation Tabs */}
      <nav className="admin-tabs">
        <div className="tab-section">
          <span className="section-label">ADVERTISER MANAGEMENT</span>
          <button 
            className={activeTab === 'content-review' ? 'active' : ''}
            onClick={() => setActiveTab('content-review')}
          >
            📝 Content Review {stats.pendingContent > 0 && <span className="badge">{stats.pendingContent}</span>}
          </button>
          <button 
            className={activeTab === 'live-content' ? 'active' : ''}
            onClick={() => setActiveTab('live-content')}
          >
            ✅ Live Content {stats.liveContent > 0 && <span className="badge green">{stats.liveContent}</span>}
          </button>
          <button 
            className={activeTab === 'advertisers' ? 'active' : ''}
            onClick={() => setActiveTab('advertisers')}
          >
            🐋 Advertisers
          </button>
          <button 
            className={activeTab === 'proof-review' ? 'active' : ''}
            onClick={() => setActiveTab('proof-review')}
          >
            🎟️ Proof Review {stats.pendingProofs > 0 && <span className="badge">{stats.pendingProofs}</span>}
          </button>
          <button 
            className={activeTab === 'promo-requests' ? 'active' : ''}
            onClick={() => setActiveTab('promo-requests')}
          >
            📦 Promo Requests {stats.pendingPromo > 0 && <span className="badge">{stats.pendingPromo}</span>}
          </button>
          <button 
            className={activeTab === 'push-notifications' ? 'active' : ''}
            onClick={() => setActiveTab('push-notifications')}
          >
            📱 Push Notifications {stats.pendingPush > 0 && <span className="badge">{stats.pendingPush}</span>}
          </button>
        </div>

        <div className="tab-section">
          <span className="section-label">QUIZ MANAGEMENT</span>
          <button 
            className={activeTab === 'daily-quiz' ? 'active' : ''}
            onClick={() => setActiveTab('daily-quiz')}
          >
            📅 Daily Quizzes
          </button>
          <button 
            className={activeTab === 'quiz-practice' ? 'active' : ''}
            onClick={() => setActiveTab('quiz-practice')}
          >
            🧪 Audio Quizzes
          </button>
        </div>

        <div className="tab-section">
          <span className="section-label">DATA MANAGEMENT</span>
          <button 
            className={activeTab === 'detections' ? 'active' : ''}
            onClick={() => setActiveTab('detections')}
          >
            🔬 Detections (CSV)
          </button>
          <button 
            className={activeTab === 'stations' ? 'active' : ''}
            onClick={() => setActiveTab('stations')}
          >
            📡 Stations
          </button>
          <button 
            className={activeTab === 'free-access' ? 'active' : ''}
            onClick={() => setActiveTab('free-access')}
          >
            🎁 Free access
          </button>
          <button 
            className={activeTab === 'app-users' ? 'active' : ''}
            onClick={() => setActiveTab('app-users')}
          >
            👥 App Users
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="admin-content">
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        )}

        {/* Sub-component tabs */}
        {!loading && activeTab === 'daily-quiz' && <AdminDailyQuiz />}
        {!loading && activeTab === 'quiz-practice' && <AdminQuizPractice />}
        {!loading && activeTab === 'stations' && <AdminStations />}
        {!loading && activeTab === 'free-access' && <AdminFreeAccess user={user} />}
        {!loading && activeTab === 'promo-requests' && <AdminPromoRequests user={user} />}
        {!loading && activeTab === 'app-users' && <AdminAppUsers />}
        {!loading && activeTab === 'detections' && <AdminDetections user={user} />}

        {/* Content Review Tab — pending only */}
        {!loading && activeTab === 'content-review' && (
          <div className="content-section">
            <div className="section-header">
              <h2>Content Review Queue</h2>
              <button className="refresh-btn" onClick={loadData}>🔄 Refresh</button>
            </div>

            {pendingContent.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">✅</span>
                <h3>All caught up!</h3>
                <p>No pending content to review.</p>
              </div>
            ) : (
              <div className="items-grid">
                {pendingContent.map(content => (
                  <div key={content.id} className="approval-card content-card">
                    {content.mediaUrl && content.mediaType === 'image' && (
                      <div className="card-image">
                        <img src={content.mediaUrl} alt={content.title} />
                      </div>
                    )}
                    {content.mediaUrl && content.mediaType === 'video' && (
                      <div className="card-image card-video">
                        <video src={content.mediaUrl} controls preload="metadata" />
                      </div>
                    )}
                    <div className="card-content">
                      <div className="card-type">{getContentTypeLabel(content.type)}</div>
                      <h3>{content.title}</h3>
                      {content.body && <p className="card-desc">{content.body}</p>}

                      {content.type === 'hot_deal' && (
                        <div className="deal-details">
                          {content.dealDiscount && <span className="deal-tag">💰 {content.dealDiscount}</span>}
                          {content.dealCode && <span className="deal-tag">🏷️ Code: {content.dealCode}</span>}
                          {content.dealExpiry && <span className="deal-tag">⏰ Expires: {formatDate(content.dealExpiry)}</span>}
                          {content.dealUrl && (
                            <a href={content.dealUrl} target="_blank" rel="noopener noreferrer" className="deal-link">
                              🔗 {content.dealUrl}
                            </a>
                          )}
                        </div>
                      )}

                      <div className="card-meta">
                        <span>By: <strong>{content.advertiserName}</strong></span>
                        <span>Cadence: {content.cadence} • Period: {content.publishPeriod}</span>
                        <span>Submitted: {formatDate(content.submittedAt || content.createdAt)}</span>
                      </div>
                      {content.species?.length > 0 && (
                        <div className="card-species">
                          🎯 Species: {displaySpecies(content.species)}
                        </div>
                      )}
                      <div className="card-actions">
                        <button className="approve-btn" onClick={() => handleApproveContent(content)}>
                          ✅ Approve
                        </button>
                        <button className="reject-btn" onClick={() => handleRejectContent(content)}>
                          ❌ Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ============================================================= */}
        {/* NEW: Live Content Tab — approved content + approved push       */}
        {/* ============================================================= */}
        {!loading && activeTab === 'live-content' && (
          <div className="content-section">
            <div className="section-header">
              <h2>Live Content</h2>
              <button className="refresh-btn" onClick={loadData}>🔄 Refresh</button>
            </div>

            {/* Approved Content */}
            <h3 className="subsection-title">Approved Content ({liveContent.length})</h3>
            {liveContent.length === 0 ? (
              <div className="empty-state small">
                <p>No approved content yet.</p>
              </div>
            ) : (
              <div className="items-grid">
                {liveContent.map(content => (
                  <div key={content.id} className="approval-card content-card live-card">
                    {content.mediaUrl && content.mediaType === 'image' && (
                      <div className="card-image">
                        <img src={content.mediaUrl} alt={content.title} />
                      </div>
                    )}
                    {content.mediaUrl && content.mediaType === 'video' && (
                      <div className="card-image card-video">
                        <video src={content.mediaUrl} controls preload="metadata" />
                      </div>
                    )}
                    <div className="card-content">
                      <div className="card-type">
                        {getContentTypeLabel(content.type)}
                        <span className="live-badge">LIVE</span>
                      </div>
                      <h3>{content.title}</h3>
                      {content.body && <p className="card-desc">{content.body}</p>}

                      {content.type === 'hot_deal' && (
                        <div className="deal-details">
                          {content.dealDiscount && <span className="deal-tag">💰 {content.dealDiscount}</span>}
                          {content.dealCode && <span className="deal-tag">🏷️ Code: {content.dealCode}</span>}
                        </div>
                      )}

                      <div className="card-meta">
                        <span>By: <strong>{content.advertiserName}</strong></span>
                        <span>Period: {content.publishPeriod}</span>
                        <span>Approved: {formatDate(content.approvedAt)}</span>
                        {content.approvedBy && <span>By: {content.approvedBy}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Approved Push Notifications */}
            <h3 className="subsection-title" style={{ marginTop: 32 }}>
              Approved Push Notifications ({approvedPush.length})
            </h3>
            {approvedPush.length === 0 ? (
              <div className="empty-state small">
                <p>No approved push notifications yet.</p>
              </div>
            ) : (
              <div className="items-grid">
                {approvedPush.map(req => (
                  <div key={req.id} className="approval-card push-card live-card">
                    <div className="card-content">
                      <div className="push-from">
                        From: <strong>{req.advertiserName}</strong>
                        <span className="live-badge">SENT</span>
                      </div>
                      <div className="push-preview">
                        <div className="push-title">{req.pushTitle || req.title}</div>
                        <div className="push-body">{req.pushBody || req.body}</div>
                      </div>
                      <div className="card-meta">
                        <span>Target: {displaySpecies(req.targetSpecies)}</span>
                        <span>Approved: {formatDate(req.approvedAt)}</span>
                        {req.approvedBy && <span>By: {req.approvedBy}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Advertisers Tab */}
        {!loading && activeTab === 'advertisers' && (
          <div className="content-section">
            <div className="section-header">
              <h2>Whale Watching Companies</h2>
              <button className="refresh-btn" onClick={loadData}>🔄 Refresh</button>
            </div>

            {advertisers.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">🐋</span>
                <h3>No advertisers yet</h3>
                <p>Whale watching companies will appear here once they sign up.</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Company</th>
                      <th>Email</th>
                      <th>Location</th>
                      <th>Species</th>
                      <th>Subscription</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {advertisers.map(adv => (
                      <tr key={adv.id}>
                        <td className="company-cell">
                          <strong>{adv.companyName || 'Unnamed'}</strong>
                        </td>
                        <td>{adv.email}</td>
                        <td>
                          {adv.location?.city 
                            ? `${adv.location.city}, ${adv.location.country}` 
                            : 'Not set'}
                        </td>
                        <td className="species-cell">
                          {displaySpecies(adv.species)}
                        </td>
                        <td>
                          <span className={`sub-badge ${(adv.products || []).includes('advertiser') && adv.portalSubscription ? 'paid' : 'free'}`}>
                            {['active', 'trialing', 'past_due'].includes(adv.portalSubscription?.status) ? '💰 Paid' : '🎟️ Free'}
                          </span>
                        </td>
                        <td>{formatDate(adv.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Proof Review Tab */}
        {!loading && activeTab === 'proof-review' && (
          <div className="content-section">
            <div className="section-header">
              <h2>Proof Review Queue</h2>
              <button className="refresh-btn" onClick={loadData}>🔄 Refresh</button>
            </div>

            {proofRequests.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">✅</span>
                <h3>All caught up!</h3>
                <p>No pending proof submissions to review.</p>
              </div>
            ) : (
              <div className="items-grid">
                {proofRequests.map(req => (
                  <div key={req.id} className="approval-card proof-card">
                    <div className="card-content">
                      <h3>{req.companyName || 'Unnamed Company'}</h3>
                      <p className="card-email">{req.email}</p>
                      <div className="card-meta">
                        <span>Uploaded: {formatDate(req.proofUploadedAt)}</span>
                      </div>
                      
                      {req.proofUrl && (
                        <div className="proof-image">
                          <a href={req.proofUrl} target="_blank" rel="noopener noreferrer">
                            <img src={req.proofUrl} alt="Proof" />
                          </a>
                          <p className="image-hint">Click to view full size</p>
                        </div>
                      )}
                      
                      <div className="card-actions">
                        <button className="approve-btn" onClick={() => handleApproveProof(req)}>
                          ✅ Approve (3 months free)
                        </button>
                        <button className="reject-btn" onClick={() => handleRejectProof(req)}>
                          ❌ Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Push Notifications Tab — pending only */}
        {!loading && activeTab === 'push-notifications' && (
          <div className="content-section">
            <div className="section-header">
              <h2>Push Notification Requests</h2>
              <span style={{ fontSize: 14, color: pushThisWeek >= PUSH_WEEKLY_LIMIT ? '#fca5a5' : '#86efac' }}>
                Approved this week: <b>{pushThisWeek} of {PUSH_WEEKLY_LIMIT}</b>
              </span>
              <button className="refresh-btn" onClick={loadData}>🔄 Refresh</button>
            </div>

            {pushRequests.length === 0 ? (
              <div className="empty-state">
                <span className="empty-icon">📱</span>
                <h3>No pending requests</h3>
                <p>Push notification requests will appear here.</p>
              </div>
            ) : (
              <div className="items-grid">
                {pushRequests.map(req => (
                  <div key={req.id} className="approval-card push-card">
                    <div className="card-content">
                      <div className="push-from">
                        From: <strong>{req.advertiserName}</strong>
                      </div>
                      <div className="push-preview">
                        <div className="push-title">{req.pushTitle || req.title}</div>
                        <div className="push-body">{req.pushBody || req.body}</div>
                      </div>
                      {req.selectedContent?.length > 0 && (
                        <div className="push-content-list">
                          <span className="pcl-label">Includes content:</span>
                          {req.selectedContent.map((c, i) => (
                            <span key={i} className="pcl-tag">{getContentTypeLabel(c.type)}: {c.title}</span>
                          ))}
                        </div>
                      )}
                      <div className="card-meta">
                        <span>Target species: {displaySpecies(req.targetSpecies)}</span>
                        {req.publishPeriod && <span>Period: {req.publishPeriod}</span>}
                        <span>Submitted: {formatDate(req.createdAt)}</span>
                      </div>
                      <div className="card-actions">
                        <button className="approve-btn" onClick={() => handleApprovePush(req)}>
                          ✅ Approve & Send
                        </button>
                        <button className="reject-btn" onClick={() => handleRejectPush(req)}>
                          ❌ Reject
                        </button>
                        <button className="reject-btn" style={{ background: 'rgba(99,102,241,0.15)', color: '#a5b4fc', borderColor: '#6366f1' }}
                          onClick={() => handleTestPush(req)}>
                          📲 Send test to my phone
                        </button>
                      </div>
                      {req.testRequest && (
                        <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, fontSize: 13,
                          background: req.testResult?.error ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)',
                          color: req.testResult?.error ? '#fca5a5' : '#86efac' }}>
                          🧪 Test to {req.testRequest.email}:{' '}
                          {!req.testResult || (req.testResult.sentAt?.toMillis?.() || 0) < (req.testRequest.requestedAt?.toMillis?.() || 0)
                            ? 'sending… (reload in a few seconds)'
                            : req.testResult.error
                              ? `not sent — ${req.testResult.error}`
                              : `sent to ${req.testResult.count} device(s)`}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
